import {
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  buildSidebarFilterRows,
  buildSidebarFiltersPayload,
  createSidebarValidationSchema,
  getSidebarFiltersCount,
} from './calendarSidebarFiltersUseCase';
import { getNonNullFilterWithType } from '../tableConditionalFilter/conditionalFiltersUseCase';
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

/**
 * @typedef {object} useCalendarSidebarFiltersReturn
 * @property {ConditionalFilter[]} sidebarFilters
 * @property {Record<string, ConditionalFilter>} sidebarFiltersById
 * @property {Array<{ lib: string, options: Array<{ filter: ConditionalFilter, index: number }> }>} sidebarFiltersGrouped
 * @property {import('react-hook-form').UseFormReturn<{ conditionalFilters: ApiManagerGlobalFiltersCreateValue[] }>} formSidebarFilters
 * @property {() => void} handleResetSidebarFilters
 * @property {() => void} handleCancelSidebarFilters
 * @property {(filterValues: Array<ApiManagerFilterValues>) => number} getSidebarFiltersCount
 * @property {(rows: ApiManagerGlobalFiltersCreateValue[]) => Array<ApiManagerFilterValues>} buildSubmitPayload
 */

/**
 * @param {object} props
 * @param {Array<ApiManagerGlobalFiltersFilter>} [props.filters]
 * @param {Array<ApiManagerFilterValues>} [props.filterValues]
 * @param {boolean} props.isLoading
 * @param {boolean} props.isSubmitting
 * @returns {useCalendarSidebarFiltersReturn}
 */
const useCalendarSidebarFilters = ({
  filters,
  filterValues,
  isLoading,
  isSubmitting,
}) => {
  const sidebarFilters = useMemo(() => {
    if (isLoading || !filters) {
      return [];
    }

    return filters.reduce(
      (acc, filter) => {
        const nonNullFilter = getNonNullFilterWithType(filter);
        return nonNullFilter ? [...acc, nonNullFilter] : acc;
      },
      /** @type {ConditionalFilter[]} */ ([]),
    );
  }, [filters, isLoading]);

  const sidebarFiltersById = useMemo(() => (
    sidebarFilters.reduce((acc, filter) => {
      acc[filter.id] = filter;
      return acc;
    }, /** @type {Record<string, ConditionalFilter>} */ ({}))
  ), [sidebarFilters]);

  const sidebarFiltersGrouped = useMemo(() => {
    const filterIndexById = sidebarFilters.reduce((acc, filter, index) => {
      acc[filter.id] = index;
      return acc;
    }, /** @type {Record<string, number>} */ ({}));

    return groupOptionsByOptGroup(sidebarFilters).map((group) => ({
      lib: group.lib,
      options: group.options.map((filter) => ({
        filter,
        index: filterIndexById[filter.id],
      })),
    }));
  }, [sidebarFilters]);

  const formSidebarFilters = useForm({
    defaultValues: {
      conditionalFilters: buildSidebarFilterRows(filters, filterValues),
    },
    resolver: zodResolver(createSidebarValidationSchema(sidebarFiltersById)),
    mode: 'onChange',
  });

  const { reset } = formSidebarFilters;

  useEffect(() => {
    if (!isLoading && !isSubmitting && filterValues) {
      reset({
        conditionalFilters: buildSidebarFilterRows(filters, filterValues),
      });
    }
  }, [filterValues, filters, isLoading, isSubmitting, reset]);

  const handleResetSidebarFilters = useCallback(() => {
    const clearedRows = buildSidebarFilterRows(filters, filterValues).map((row) => ({
      ...row,
      values: [],
    }));

    formSidebarFilters.reset({ conditionalFilters: clearedRows });
  }, [filters, filterValues, formSidebarFilters]);

  const handleCancelSidebarFilters = useCallback(() => {
    formSidebarFilters.reset({
      conditionalFilters: buildSidebarFilterRows(filters, filterValues),
    });
  }, [filters, filterValues, formSidebarFilters]);

  const buildSubmitPayload = useCallback((rows) => (
    buildSidebarFiltersPayload(rows, sidebarFiltersById, filterValues)
  ), [filterValues, sidebarFiltersById]);

  const getSidebarFiltersCountCallback = useCallback(
    (/** @type {Array<ApiManagerFilterValues>} */ values) => getSidebarFiltersCount(values),
    [],
  );

  return {
    sidebarFilters,
    sidebarFiltersById,
    sidebarFiltersGrouped,
    formSidebarFilters,
    handleResetSidebarFilters,
    handleCancelSidebarFilters,
    getSidebarFiltersCount: getSidebarFiltersCountCallback,
    buildSubmitPayload,
  };
};

export default useCalendarSidebarFilters;
